<script lang="ts" setup>
import { Button } from '~ds/components/ui/button';
import { Heading, Text } from '~ds/components/ui/typography';
import { PasswordForgotAction } from '~kui/types/auth';
import { useI18n } from 'vue-i18n';

interface Props {
  email: string;
  loading: boolean;
  forgotAction?: PasswordForgotAction;
}

withDefaults(defineProps<Props>(), {
  forgotAction: PasswordForgotAction.Code,
});

const emit = defineEmits<{
  submit: [password: string];
  forgot: [];
  back: [];
}>();

const { t } = useI18n();

const password = ref('');
const errorMessages = ref<string[]>([]);

const handleSubmit = () => {
  if (!password.value) {
    errorMessages.value = [t('kui.password.required')];
    return;
  }
  errorMessages.value = [];
  emit('submit', password.value);
};

const setError = (message: string) => {
  errorMessages.value = [message];
};

defineExpose({ setError });
</script>

<template>
  <form
    class="flex flex-col items-center"
    data-testid="auth-password-step"
    @submit.prevent="handleSubmit"
  >
    <Heading
      size="xl"
      as="h1"
      class="text-center mb-3"
    >
      {{ t('kui.auth.enterYourPassword') }}
    </Heading>
    <Text size="lg" class="text-center font-medium">
      {{ email }}
    </Text>
    <Button
      class="mb-6"
      variant="link"
      size="sm"
      type="button"
      :disabled="loading"
      data-testid="auth-password-back"
      @click="emit('back')"
    >
      {{ t('general.auth.changeEmailButton') }}
    </Button>
    <KUICoreHiddenUsernameField :email="email" />
    <div class="w-full">
      <KUICoreInputText
        v-model="password"
        :label="t('kui.password.label')"
        hide-label
        type="password"
        autocomplete="current-password"
        :placeholder="t('kui.password.placeholder')"
        :error-messages="errorMessages"
        required
        data-testid="auth-password-field"
      />
    </div>
    <Button
      class="mt-6 w-full"
      variant="secondary"
      size="lg"
      type="submit"
      :loading="loading"
      data-testid="auth-password-submit"
    >
      {{ t('general.auth.submitButton.submit') }}
    </Button>
    <Button
      class="mt-4"
      variant="link"
      size="sm"
      type="button"
      :disabled="loading"
      data-testid="auth-password-forgot"
      @click="emit('forgot')"
    >
      {{ forgotAction === PasswordForgotAction.Reset ? t('kui.auth.forgotPassword') : t('kui.auth.forgotPasswordUseCode') }}
    </Button>
  </form>
</template>
